import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import { GenericError } from "@via/components/error";
import InlineInput from "@via/components/inputs/inline-input";
import Table, { TableSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import useUrlParams from "@via/hooks/useURLParams";
import CreateUserGroupForm from "@via/pages/account/profile/user-groups/components/create-user-group-form.tsx";
import UserGroupFilter from "@via/pages/account/profile/user-groups/components/user-group-filter";
import {
  BUTTONS,
  ERROR,
  USER_GROUPS_TABLE,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { GetUserGroupsResponse } from "common";
import { type JSX, useEffect, useState } from "react";
import DeleteUserGroupModal from "@via/pages/account/profile/user-groups/components/delete-user-group-modal";
import UserGroupMemberModal from "@via/pages/account/profile/user-groups/components/user-group-member-modal";
import {
  useUpdateUserGroupName,
  useUserGroups,
} from "@via/api/user-groups.api";
import { useAuth } from "@via/hooks/useAuth";

const columnHelper = createColumnHelper<GetUserGroupsResponse>();

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

export default function UserGroups(): JSX.Element {
  const [editableRowId, setEditableRowId] = useState<number | null>(null);
  const [userGroupToBeDeleted, setUserGroupToBeDeleted] =
    useState<GetUserGroupsResponse | null>(null);
  const [toBeViewedGroup, setToBeViewedGroup] =
    useState<GetUserGroupsResponse | null>(null);

  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);

  const { data, isLoading, isError } = useUserGroups(
    page,
    pageSize,
    sort,
    columnFilters
  );

  const {
    mutate: updateUserGroupName,
    isPending: isUpdateUserGroupNameLoading,
    isSuccess: isUpdateUserGroupNameSuccess,
  } = useUpdateUserGroupName();

  const { user } = useAuth();

  const handleAction = (
    option: TableContextMenuInterface,
    group: GetUserGroupsResponse
  ): void => {
    switch (option.name) {
      case "Edit Name":
        setEditableRowId(group.id);
        break;
      case "Delete":
        setUserGroupToBeDeleted(group);
        break;
      case "Edit/View Group":
        setToBeViewedGroup(group);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isUpdateUserGroupNameSuccess) {
      setEditableRowId(null);
    }
  }, [isUpdateUserGroupNameSuccess]);

  const columns = [
    columnHelper.accessor("id", {
      cell: info => info.getValue(),
      header: () => <span>Id</span>,
    }),
    columnHelper.accessor("name", {
      cell: info =>
        editableRowId === info.row.original.id ? (
          <InlineInput
            value={info.getValue()}
            isLoading={isUpdateUserGroupNameLoading}
            onSubmit={name => {
              updateUserGroupName({
                id: info.row.original.id.toString(),
                name,
              });
            }}
            onCancel={() => setEditableRowId(null)}
          />
        ) : (
          <span>{info.getValue()}</span>
        ),
      header: () => <span>Group Name</span>,
    }),
    columnHelper.accessor("owner", {
      cell: info => info.getValue(),
      header: () => <span>Owner</span>,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Created At</span>,
    }),
    columnHelper.accessor("id", {
      cell: info => {
        const own = info.row.original.own;
        const isAdmin = user.userRole === USER_ROLES.ADMIN;
        const isSystemGenerated = info.row.original.isSystemGenerated;
        /*
         * Logic:
         * - System Admin always see all options regardless of systemGenerated status
         * - For non system admin users:
         *   - If systemGenerated = true → No options available
         *   - If systemGenerated = false → Normal permission logic applies
         */
        let filteredOptions;
        if (isAdmin) {
          // Admin always sees all options
          filteredOptions = USER_GROUPS_TABLE;
        } else if (isSystemGenerated) {
          // System generated groups show no options for non-admin users
          filteredOptions = [];
        } else {
          // Normal permission logic for non-system generated groups
          filteredOptions = own
            ? USER_GROUPS_TABLE
            : USER_GROUPS_TABLE.filter(
                option => option.name === "Edit/View Group"
              );
        }

        if (filteredOptions && filteredOptions.length === 0) return null;

        return (
          <div data-testid={`userGroupOptions-${info.row.original.id}`}>
            <TableContextMenu
              options={filteredOptions}
              onSelect={option => handleAction(option, info.row.original)}
            />
          </div>
        );
      },
      header: () => <span>Options</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<GetUserGroupsResponse>>;

  useEffect(() => {
    setUrlParams({
      sort,
      filters: columnFilters,
      page,
      pageSize,
    });
  }, [sort, columnFilters, page, pageSize]);

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">User Groups</h2>
        <ActionButton
          button={BUTTONS.CREATE_GROUP}
          onClick={() => {
            setDrawerOpen(true);
          }}
        />
      </div>

      {isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isLoading}
          data={data?.data}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={data?.total}
          size={TableSize.Big}
          additionalFilters={
            <UserGroupFilter
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          }
        />
      )}
      {userGroupToBeDeleted && (
        <DeleteUserGroupModal
          userGroup={userGroupToBeDeleted}
          setUserGroupToBeDeleted={() => {
            setUserGroupToBeDeleted(null);
          }}
        />
      )}
      <Drawer
        id="create-user-group"
        open={isDrawerOpen}
        setOpen={setDrawerOpen}>
        <CreateUserGroupForm setOpen={setDrawerOpen} />
      </Drawer>
      {toBeViewedGroup && (
        <UserGroupMemberModal
          group={toBeViewedGroup}
          setToBeViewedGroup={setToBeViewedGroup}
          userRole={user?.userRole}
        />
      )}
    </div>
  );
}
